import { reactive } from "vue";
import { getCategoryLanguageName, CategoryName } from "@/locales/i18n";
import ResearchCategoryRepositoryAPI from "./research-category-repository-api";

interface BaseCategory {
  id: string;
  name: string;
}
interface TreeViewCategory {
  key: string;
  label: string;
  data: string;
  item: any;
  children?: TreeViewCategory[];
}

const state = reactive({
  researchInfrastructureCount: 0,
  adminFacultyCount: 0,
  adminEquipmentCount: 0,
  equipmentCount: 0,
  researchEntityCount: 0,
  userCount: 0,
  researchGroupCount: 0,
  researchOrganizationCount: 0,

  accessPurposes: [],
  accessTypes: [],
  accessPurposeGroup: [],
  lifeStatusCodes: [],
  accessMethods: [],
  cities: [],
  cityGroup: [],

  researcherExpertFields: [],
  researcherTitles: [],
  languages: [],

  researcherStatuses: [],
  equipmentReservationStatuses: [],
  equipmentFundingTypes: [],
  researcherWorkStatuses: [],

  equipmentResearchOrientationTypes: [],
  equipmentCriterion: [],
  uniLeedClassTypes: [],
  userRoles: [],
}) as any;

export default function ResearchCategoryFiltersData() {
  async function loadResearchEquipmentCategories(): Promise<void> {
    const { loadCategories } = ResearchCategoryRepositoryAPI();
    const researchEquipmentCategories = await loadCategories();
    setResearchEquipmentCategories(researchEquipmentCategories);
  }

  const dropdownDisplayData = (
    item: any,
    propertyKey = CategoryName.ENG_NAME
  ): BaseCategory => {
    return {
      id: item.id,
      name: item[propertyKey],
    } as BaseCategory;
  };

  const singleLayeredCategoryDisplayData = (
    item: any,
    categoryName = "Category",
    propertyKey = CategoryName.ENG_NAME
  ): TreeViewCategory => {
    return {
      key: item.id,
      label: item[propertyKey],
      data: categoryName,
      item: item,
    } as TreeViewCategory;
  };

  const threeLayeredCategoryDisplayData = (
    item: any,
    key = CategoryName.ENG_NAME,
    categoryName = "Category"
  ): TreeViewCategory => {
    return {
      key: `class:${item.id}`,
      label: item[key],
      data: categoryName,
      children: item.equipmentUniLeedOrderTypes.map((item: any) => {
        return {
          key: `order:${item.id}`,
          label: item[key],
          data: categoryName,
          children: item.equipmentUniLeedTypes.map((item: any) => {
            return {
              key: `type:${item.id}`,
              label: item[key],
              data: categoryName,
            };
          }),
        } as TreeViewCategory;
      }),
    } as TreeViewCategory;
  };

  const setResearchEquipmentCategories = (data: any): void => {
    const localeProperty = getCategoryLanguageName();
    state.accessPurposes = data.accessPurposes.map((item: any) =>
      singleLayeredCategoryDisplayData(item, "Access purpose", localeProperty)
    );
    state.accessTypes = data.accessTypes.map((item: any) =>
      singleLayeredCategoryDisplayData(item, "Access type", localeProperty)
    );
    state.lifeStatusCodes = data.lifeStatusCodes.map((item: any) =>
      singleLayeredCategoryDisplayData(item, "Life status", localeProperty)
    );
    state.accessMethods = data.accessMethods.map((item: any) =>
      singleLayeredCategoryDisplayData(item, "Access method", localeProperty)
    );

    state.cities = data.cities.map((item: any) =>
      singleLayeredCategoryDisplayData(item, "Location", CategoryName.CITY_NAME)
    );

    state.cityGroup = data.cities.map((item: any) =>
      dropdownDisplayData(item, CategoryName.CITY_NAME)
    );

    state.researcherExpertFields = data.researcherExpertFields.map(
      (item: any) => dropdownDisplayData(item, localeProperty)
    );

    state.researcherTitles = data.researcherTitles.map((item: any) =>
      dropdownDisplayData(item, localeProperty)
    );

    state.languages = data.languages.map((item: any) =>
      dropdownDisplayData(item, localeProperty)
    );

    state.equipmentReservationStatuses = data.equipmentReservationStatuses.map(
      (item: any) => {
        return dropdownDisplayData(item, localeProperty);
      }
    );

    state.equipmentFundingTypes = data.equipmentFundingTypes.map((item: any) =>
      dropdownDisplayData(item, localeProperty)
    );

    state.researcherStatuses = data.researcherStatuses.map((item: any) =>
      dropdownDisplayData(item, localeProperty)
    );

    state.researcherWorkStatuses = data.researcherWorkStatuses.map(
      (item: any) => {
        return dropdownDisplayData(item, localeProperty);
      }
    );

    state.accessPurposeGroup = data.accessPurposes.map((item: any) => {
      return dropdownDisplayData(item, localeProperty);
    });

    state.equipmentResearchOrientationTypes =
      data.equipmentResearchOrientationTypes.map((item: any) =>
        singleLayeredCategoryDisplayData(item, "Category", localeProperty)
      );
    state.equipmentCriterion = data.equipmentCriterion.map((item: any) =>
      singleLayeredCategoryDisplayData(item, "Category", localeProperty)
    );
    state.uniLeedClassTypes = data.uniLeedClassTypes.map((item: any) =>
      threeLayeredCategoryDisplayData(
        item.equipmentUniLeedClassType,
        localeProperty
      )
    );
    state.equipmentCount = data.equipmentCount;
    state.researchEntityCount = data.researchEntityCount;
    state.userCount = data.userCount;
    state.researchGroupCount = data.researchGroupCount;
    state.researchOrganizationCount = data.researchOrganizationCount;
    state.researchInfrastructureCount = data.researchInfrastructureCount;
    state.adminEquipmentCount = data.adminEquipmentCount;
    state.adminFacultyCount = data.adminFacultyCount;
    state.userRoles = data.userRoles.map((item: any) => {
      return dropdownDisplayData(item, localeProperty);
    });
  };

  return {
    loadResearchEquipmentCategories,
    getResearchEquipmentCategories: state,
  };
}
